import _ from "underscore";
import classNames from "classnames";
import PropTypes from "prop-types";
import React from "react";

const { any, string, func, bool, number } = PropTypes;

// this is an unstyled input that...

// * onChange is passed new value directly
// * debounced by 200ms by default (prop = debounceTimeout)
// * focusOnMount prop is available
// * console.error when uncontrolled (and will intentionally behave oddly)

const warn = (propValue, stateValue) => {
  window.console.error("value mismatch. Input should be controlled", {
    propValue,
    stateValue,
  });
};

const focusRef = ref => {
  if (ref && ref.current && typeof ref.current.focus === "function") {
    ref.current.focus();
  }
};

const propTypes = {
  className: string,
  onChange: func.isRequired,
  onChangeEvent: func,
  value: string,
  debounceTimeout: number,
  focusOnMount: bool,
  forwardedRef: any,
};

const PROP_KEYS = _.keys(propTypes);

class Input extends React.Component {
  static displayName = "Input";

  static propTypes = propTypes;

  static defaultProps = {
    onChange: _.noop,
    onChangeEvent: _.noop,
    value: "",
    debounceTimeout: 200,
  };

  state = {
    value: this.props.value,
    ahead: false,
  };

  inputRef = React.createRef();

  componentDidMount() {
    this.warn = _.once(warn);

    this.debouncedSendChange = _.debounce(this.sendChange, this.props.debounceTimeout);

    if (this.props.focusOnMount) {
      focusRef(this.props.forwardedRef || this.inputRef);
    }
  }

  componentWillUnmount() {
    this.unmounted = true;
    this.clearTimeout();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.value !== this.props.value) {
      this.setState(state => ({ value: this.props.value }));
    }

    if (prevState.ahead && !this.state.ahead) {
      if (this.state.value !== this.props.value) {
        this.warn(this.props.value, this.state.value);
      }
    }
  }

  sendChange = () => {
    if (!this.unmounted) {
      this.props.onChange(this.state.value);

      this.setState(() => ({ ahead: false }));
    }
  };

  clearTimeout = () => {
    this.timeout && this.timeout.cancel && this.timeout.cancel();
  };

  handleChange = evt => {
    const value = evt.target.value;
    this.props.onChangeEvent(evt);
    this.clearTimeout();
    this.setState(() => ({ value: value, ahead: true }));
    this.timeout = this.debouncedSendChange();
  };

  render() {
    const rest = _.omit(this.props, PROP_KEYS);

    const value = this.state.ahead ? this.state.value : this.props.value;

    return (
      <input
        {...rest}
        ref={this.props.forwardedRef || this.inputRef}
        className={classNames("et--input", this.props.className)}
        value={value || ""}
        onChange={this.handleChange}
      />
    );
  }
}

export default React.forwardRef((props, ref) => <Input {...props} forwardedRef={ref} />);
